import React, { useState, useEffect } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import "../static/css/AdminPublications.css";
import API_URL from "../config";

const defaultData = {
  companyName: "",
  address: "",
  companyPicture: null,
  cin: "",
  companyProfile: "",
  quickInfo: { name: "", address: "", cin: "", picture: null },
  videoUrl: "",
  pdfDocuments: [],
  sliderImages: [],
  whoWeAre: { description: "", email: "", phone: "" },
  attachments: { orders: [], eAuction: [], stakeholders: [], assetsPhotos: [] },
};

const AdminInsolvencyCasePage = ({ slug, title, color }) => {
  const [caseData, setCaseData] = useState(defaultData);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    fetchData();
  }, [slug]);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/insolvency-cases/${slug}`);
      if (res.ok) {
        const data = await res.json();
        setCaseData({ ...defaultData, ...data });
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/insolvency-cases/${slug}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(caseData),
      });
      if (res.ok) {
        alert("Data updated successfully!");
        setEditMode(false);
      } else {
        alert("Failed to update. Please try again.");
      }
    } catch (err) {
      console.error("Error saving:", err);
      alert("Error saving data.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setCaseData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (section, field, value) => {
    setCaseData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  // File upload helper — returns full URL
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await fetch(`${API_URL}/api/upload`, {
      method: "POST",
      body: formData,
    });
    if (!res.ok) throw new Error("Upload failed");
    const data = await res.json();
    return `${API_URL}${data.fileUrl}`;
  };

  // Image upload for a simple field
  const handleImageUpload = async (field, file) => {
    try {
      const url = await uploadFile(file);
      handleChange(field, url);
      alert("Image uploaded!");
    } catch (err) {
      console.error(err);
      alert("Upload failed");
    }
  };

  // Nested image upload (quickInfo.picture)
  const handleNestedImageUpload = async (section, field, file) => {
    try {
      const url = await uploadFile(file);
      handleNestedChange(section, field, url);
      alert("Image uploaded!");
    } catch (err) {
      console.error(err);
      alert("Upload failed");
    }
  };

  // Slider
  const handleSliderUpload = async (file) => {
    try {
      const url = await uploadFile(file);
      setCaseData((prev) => ({
        ...prev,
        sliderImages: [
          ...prev.sliderImages,
          { url, name: file.name, uploadDate: new Date().toISOString() },
        ],
      }));
      alert("Slider image added!");
    } catch (err) {
      console.error(err);
      alert("Upload failed");
    }
  };

  const deleteSliderImage = (index) => {
    if (window.confirm("Delete this slider image?")) {
      setCaseData((prev) => ({
        ...prev,
        sliderImages: prev.sliderImages.filter((_, i) => i !== index),
      }));
    }
  };

  // PDF
  const handlePdfUpload = async (file) => {
    try {
      const url = await uploadFile(file);
      setCaseData((prev) => ({
        ...prev,
        pdfDocuments: [
          ...prev.pdfDocuments,
          { name: file.name, url, uploadDate: new Date().toISOString() },
        ],
      }));
      alert("PDF uploaded!");
    } catch (err) {
      console.error(err);
      alert("Upload failed");
    }
  };

  const deletePdf = (index) => {
    if (window.confirm("Delete this PDF?")) {
      setCaseData((prev) => ({
        ...prev,
        pdfDocuments: prev.pdfDocuments.filter((_, i) => i !== index),
      }));
    }
  };

  // Attachments
  const handleAttachmentUpload = async (category, file) => {
    try {
      const url = await uploadFile(file);
      setCaseData((prev) => ({
        ...prev,
        attachments: {
          ...prev.attachments,
          [category]: [
            ...prev.attachments[category],
            { name: file.name, url, uploadDate: new Date().toISOString() },
          ],
        },
      }));
      alert("File uploaded!");
    } catch (err) {
      console.error(err);
      alert("Upload failed");
    }
  };

  const deleteAttachment = (category, index) => {
    if (window.confirm("Delete this file?")) {
      setCaseData((prev) => ({
        ...prev,
        attachments: {
          ...prev.attachments,
          [category]: prev.attachments[category].filter((_, i) => i !== index),
        },
      }));
    }
  };

  // YouTube embed helper
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    return match ? match[1] : null;
  };

  const sliderImages = caseData.sliderImages || [];

  return (
    <div
      className="admin-container"
      style={{ padding: "2rem", maxWidth: "1400px", margin: "0 auto" }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "2rem",
          borderBottom: `3px solid ${color}`,
          paddingBottom: "1rem",
        }}
      >
        <h2 style={{ color, fontSize: "2rem", margin: 0 }}>{title}</h2>
        <div style={{ display: "flex", gap: "1rem" }}>
          {!editMode ? (
            <button onClick={() => setEditMode(true)} style={btnStyle(color)}>
              Edit Content
            </button>
          ) : (
            <>
              <button
                onClick={handleSave}
                disabled={loading}
                style={btnStyle(loading ? "#ccc" : "#4CAF50")}
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
              <button
                onClick={() => {
                  setEditMode(false);
                  fetchData();
                }}
                disabled={loading}
                style={btnStyle("#f44336")}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* 1. Basic Company Info */}
      <Section heading="Basic Company Information" color={color}>
        <Field
          label="1. Company Name"
          value={caseData.companyName}
          editMode={editMode}
          onChange={(v) => handleChange("companyName", v)}
          color={color}
        />
        <Field
          label="2. Address"
          value={caseData.address}
          editMode={editMode}
          onChange={(v) => handleChange("address", v)}
          color={color}
          multiline
        />
        <div>
          <label style={labelStyle}>3. Company Picture</label>
          {editMode && (
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                e.target.files[0] &&
                handleImageUpload("companyPicture", e.target.files[0])
              }
              style={fileInputStyle(color)}
            />
          )}
          {caseData.companyPicture && (
            <img
              src={caseData.companyPicture}
              alt="Company"
              style={{
                maxWidth: "400px",
                borderRadius: "8px",
                marginTop: "0.5rem",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            />
          )}
        </div>
        <Field
          label="4. CIN"
          value={caseData.cin}
          editMode={editMode}
          onChange={(v) => handleChange("cin", v)}
          color={color}
          mono
        />
        <Field
          label="5. Company Profile"
          value={caseData.companyProfile}
          editMode={editMode}
          onChange={(v) => handleChange("companyProfile", v)}
          color={color}
          multiline
        />
      </Section>

      {/* 2. Quick Info */}
      <Section heading="Quick Info" color={color}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "1.5rem",
          }}
        >
          <Field
            label="Name"
            value={caseData.quickInfo.name}
            editMode={editMode}
            onChange={(v) => handleNestedChange("quickInfo", "name", v)}
            color={color}
          />
          <Field
            label="Address"
            value={caseData.quickInfo.address}
            editMode={editMode}
            onChange={(v) => handleNestedChange("quickInfo", "address", v)}
            color={color}
          />
          <Field
            label="CIN"
            value={caseData.quickInfo.cin}
            editMode={editMode}
            onChange={(v) => handleNestedChange("quickInfo", "cin", v)}
            color={color}
          />
          <div>
            <label style={labelStyle}>Picture</label>
            {editMode && (
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  e.target.files[0] &&
                  handleNestedImageUpload(
                    "quickInfo",
                    "picture",
                    e.target.files[0]
                  )
                }
                style={fileInputStyle(color)}
              />
            )}
            {caseData.quickInfo.picture && (
              <img
                src={caseData.quickInfo.picture}
                alt="Quick Info"
                style={{
                  maxWidth: "150px",
                  borderRadius: "6px",
                  marginTop: "0.5rem",
                }}
              />
            )}
          </div>
        </div>
      </Section>

      {/* 3. Image Slider */}
      <Section heading="Image Slider" color={color}>
        {editMode && (
          <div style={{ marginBottom: "1.5rem" }}>
            <label style={{ ...btnStyle(color), display: "inline-block", cursor: "pointer" }}>
              + Add Slider Image
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  e.target.files[0] && handleSliderUpload(e.target.files[0])
                }
                style={{ display: "none" }}
              />
            </label>
          </div>
        )}

        {sliderImages.length > 0 ? (
          <>
            {/* Slider Preview */}
            <div
              style={{
                position: "relative",
                width: "100%",
                maxWidth: "800px",
                margin: "0 auto 1.5rem",
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              }}
            >
              <img
                src={sliderImages[currentSlide]?.url}
                alt={`Slide ${currentSlide + 1}`}
                style={{
                  width: "100%",
                  height: "400px",
                  objectFit: "cover",
                  display: "block",
                }}
              />
              {sliderImages.length > 1 && (
                <>
                  <button
                    onClick={() =>
                      setCurrentSlide((p) =>
                        p === 0 ? sliderImages.length - 1 : p - 1
                      )
                    }
                    style={sliderBtnStyle("left")}
                  >
                    &#8249;
                  </button>
                  <button
                    onClick={() =>
                      setCurrentSlide((p) =>
                        p === sliderImages.length - 1 ? 0 : p + 1
                      )
                    }
                    style={sliderBtnStyle("right")}
                  >
                    &#8250;
                  </button>
                </>
              )}
              <div
                style={{
                  position: "absolute",
                  bottom: "12px",
                  width: "100%",
                  textAlign: "center",
                }}
              >
                {sliderImages.map((_, i) => (
                  <span
                    key={i}
                    onClick={() => setCurrentSlide(i)}
                    style={{
                      display: "inline-block",
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      background: i === currentSlide ? "#fff" : "rgba(255,255,255,0.5)",
                      margin: "0 4px",
                      cursor: "pointer",
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Thumbnails with delete */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                gap: "1rem",
              }}
            >
              {sliderImages.map((img, i) => (
                <div
                  key={i}
                  style={{
                    position: "relative",
                    borderRadius: "8px",
                    overflow: "hidden",
                    border:
                      i === currentSlide
                        ? `3px solid ${color}`
                        : "3px solid transparent",
                    cursor: "pointer",
                  }}
                  onClick={() => setCurrentSlide(i)}
                >
                  <img
                    src={img.url}
                    alt={img.name}
                    style={{
                      width: "100%",
                      height: "100px",
                      objectFit: "cover",
                    }}
                  />
                  {editMode && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteSliderImage(i);
                      }}
                      style={{
                        position: "absolute",
                        top: "4px",
                        right: "4px",
                        background: "#f44336",
                        color: "#fff",
                        border: "none",
                        borderRadius: "50%",
                        width: "24px",
                        height: "24px",
                        cursor: "pointer",
                        fontSize: "14px",
                        lineHeight: "24px",
                        padding: 0,
                      }}
                    >
                      X
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <div
            style={{
              padding: "3rem",
              textAlign: "center",
              color: "#999",
              background: "#f9f9f9",
              borderRadius: "8px",
            }}
          >
            No slider images added yet
          </div>
        )}
      </Section>

      {/* 4. Video */}
      <Section heading="Video" color={color}>
        <Field
          label="Video URL (YouTube or Direct Link)"
          value={caseData.videoUrl}
          editMode={editMode}
          onChange={(v) => handleChange("videoUrl", v)}
          color={color}
          placeholder="https://www.youtube.com/watch?v=..."
        />
        {caseData.videoUrl && (
          <div style={{ marginTop: "1rem" }}>
            {getYouTubeId(caseData.videoUrl) ? (
              <iframe
                width="100%"
                height="400"
                src={`https://www.youtube.com/embed/${getYouTubeId(caseData.videoUrl)}`}
                title="Video Preview"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ borderRadius: "8px", maxWidth: "700px" }}
              />
            ) : (
              <video
                controls
                src={caseData.videoUrl}
                style={{
                  maxWidth: "700px",
                  width: "100%",
                  borderRadius: "8px",
                }}
              />
            )}
          </div>
        )}
      </Section>

      {/* 5. PDF Documents */}
      <Section heading="PDF Documents" color={color}>
        {editMode && (
          <div style={{ marginBottom: "1.5rem" }}>
            <label style={{ ...btnStyle(color), display: "inline-block", cursor: "pointer" }}>
              + Upload PDF
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) =>
                  e.target.files[0] && handlePdfUpload(e.target.files[0])
                }
                style={{ display: "none" }}
              />
            </label>
          </div>
        )}
        <FileList
          files={caseData.pdfDocuments}
          editMode={editMode}
          onDelete={deletePdf}
          color={color}
        />
      </Section>

      {/* 6. Who We Are */}
      <Section heading="Who We Are" color={color}>
        <div>
          <label style={labelStyle}>Description</label>
          {editMode ? (
            <div className="rich-text-editor" style={{ marginBottom: "1rem" }}>
              <CKEditor
                editor={ClassicEditor}
                data={caseData.whoWeAre.description || ""}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  handleNestedChange("whoWeAre", "description", data);
                }}
                config={{
                  toolbar: [
                    "heading",
                    "|",
                    "bold",
                    "italic",
                    "link",
                    "bulletedList",
                    "numberedList",
                    "blockQuote",
                    "undo",
                    "redo",
                  ],
                }}
              />
            </div>
          ) : (
            <div
              style={{
                padding: "0.75rem",
                background: "#f5f5f5",
                borderRadius: "6px",
                fontSize: "1rem",
                lineHeight: "1.6",
                minHeight: "1.5rem",
              }}
              dangerouslySetInnerHTML={{ __html: caseData.whoWeAre.description || '<span style="color: #ccc">Not set</span>' }}
            />
          )}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "1.5rem",
          }}
        >
          <Field
            label="Email"
            value={caseData.whoWeAre.email}
            editMode={editMode}
            onChange={(v) => handleNestedChange("whoWeAre", "email", v)}
            color={color}
            type="email"
          />
          <Field
            label="Phone"
            value={caseData.whoWeAre.phone}
            editMode={editMode}
            onChange={(v) => handleNestedChange("whoWeAre", "phone", v)}
            color={color}
            type="tel"
          />
        </div>
      </Section>

      {/* 7. Attachments */}
      <Section heading="Attachments" color={color}>
        <AttachmentSection
          title="Orders"
          category="orders"
          files={caseData.attachments.orders}
          editMode={editMode}
          onUpload={(f) => handleAttachmentUpload("orders", f)}
          onDelete={(i) => deleteAttachment("orders", i)}
          color={color}
        />
        <AttachmentSection
          title="E-Auction"
          category="eAuction"
          files={caseData.attachments.eAuction}
          editMode={editMode}
          onUpload={(f) => handleAttachmentUpload("eAuction", f)}
          onDelete={(i) => deleteAttachment("eAuction", i)}
          color={color}
        />
        <AttachmentSection
          title="List of Stakeholders"
          category="stakeholders"
          files={caseData.attachments.stakeholders}
          editMode={editMode}
          onUpload={(f) => handleAttachmentUpload("stakeholders", f)}
          onDelete={(i) => deleteAttachment("stakeholders", i)}
          color={color}
        />
        <AttachmentSection
          title="Assets Photos"
          category="assetsPhotos"
          files={caseData.attachments.assetsPhotos}
          editMode={editMode}
          onUpload={(f) => handleAttachmentUpload("assetsPhotos", f)}
          onDelete={(i) => deleteAttachment("assetsPhotos", i)}
          color={color}
        />
      </Section>

      {/* Preview Link */}
      <div
        style={{
          background: "#e8f5e9",
          borderRadius: "8px",
          padding: "1.5rem",
          borderLeft: "4px solid #4CAF50",
        }}
      >
        <h4 style={{ color: "#2e7d32", marginBottom: "0.5rem" }}>
          Live Preview
        </h4>
        <p style={{ margin: "0.5rem 0", color: "#555" }}>
          View the live page:{" "}
          <a
            href={`http://ibcupdates.com/${slug}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color, fontWeight: "600" }}
          >
            ibcupdates.com/{slug}
          </a>
        </p>
        <p
          style={{ margin: "0.5rem 0 0 0", fontSize: "0.9rem", color: "#666" }}
        >
          Changes are reflected immediately after saving.
        </p>
      </div>
    </div>
  );
};

/* ======================== Reusable Sub-components ======================== */

const Section = ({ heading, color, children }) => (
  <div
    style={{
      background: "#fff",
      borderRadius: "12px",
      padding: "2rem",
      marginBottom: "2rem",
      boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    }}
  >
    <h3
      style={{ color, marginBottom: "1.5rem", fontSize: "1.5rem" }}
    >
      {heading}
    </h3>
    <div style={{ display: "grid", gap: "1.5rem" }}>{children}</div>
  </div>
);

const Field = ({
  label,
  value,
  editMode,
  onChange,
  color,
  multiline,
  rows = 3,
  mono,
  type = "text",
  placeholder,
}) => (
  <div>
    <label style={labelStyle}>{label}</label>
    {editMode ? (
      multiline ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          style={{
            width: "100%",
            padding: "0.75rem",
            border: `2px solid ${color}`,
            borderRadius: "6px",
            fontSize: "1rem",
            fontFamily: "inherit",
          }}
        />
      ) : (
        <input
          type={type}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: "100%",
            padding: "0.75rem",
            border: `2px solid ${color}`,
            borderRadius: "6px",
            fontSize: "1rem",
          }}
        />
      )
    ) : (
      <div
        style={{
          padding: "0.75rem",
          background: "#f5f5f5",
          borderRadius: "6px",
          fontSize: "1rem",
          lineHeight: "1.6",
          fontFamily: mono ? "monospace" : "inherit",
          minHeight: "1.5rem",
        }}
      >
        {value || <span style={{ color: "#ccc" }}>Not set</span>}
      </div>
    )}
  </div>
);

const FileList = ({ files, editMode, onDelete, color }) => (
  <div style={{ display: "grid", gap: "0.75rem" }}>
    {files.map((file, index) => (
      <div
        key={index}
        style={{
          padding: "0.75rem 1rem",
          background: "#f9f9f9",
          borderRadius: "6px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ fontWeight: "600", color: "#333", fontSize: "0.95rem" }}>
            {file.name}
          </div>
          <div style={{ fontSize: "0.8rem", color: "#666", marginTop: "0.25rem" }}>
            {file.uploadDate
              ? new Date(file.uploadDate).toLocaleDateString()
              : ""}
          </div>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            onClick={() => window.open(file.url, "_blank")}
            style={smallBtnStyle("#2196F3")}
          >
            View
          </button>
          {editMode && (
            <button onClick={() => onDelete(index)} style={smallBtnStyle("#f44336")}>
              Delete
            </button>
          )}
        </div>
      </div>
    ))}
    {files.length === 0 && (
      <div
        style={{
          padding: "1.5rem",
          textAlign: "center",
          color: "#999",
          fontSize: "0.9rem",
        }}
      >
        No files added yet
      </div>
    )}
  </div>
);

const AttachmentSection = ({
  title,
  files,
  editMode,
  onUpload,
  onDelete,
  color,
}) => (
  <div style={{ marginBottom: "1.5rem" }}>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "1rem",
        paddingBottom: "0.5rem",
        borderBottom: "2px solid #e0e0e0",
      }}
    >
      <h4 style={{ margin: 0, color: "#333", fontSize: "1.1rem" }}>
        {title}
      </h4>
      {editMode && (
        <label
          style={{
            ...btnStyle(color),
            fontSize: "0.9rem",
            padding: "0.5rem 1rem",
            display: "inline-block",
            cursor: "pointer",
          }}
        >
          + Add File
          <input
            type="file"
            onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])}
            style={{ display: "none" }}
          />
        </label>
      )}
    </div>
    <FileList files={files} editMode={editMode} onDelete={onDelete} color={color} />
  </div>
);

/* ======================== Style Helpers ======================== */

const labelStyle = {
  display: "block",
  marginBottom: "0.5rem",
  color: "#666",
  fontWeight: "600",
};

const btnStyle = (bg) => ({
  padding: "0.75rem 1.5rem",
  background: bg,
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "1rem",
  fontWeight: "600",
});

const smallBtnStyle = (bg) => ({
  padding: "0.4rem 0.75rem",
  background: bg,
  color: "#fff",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
  fontSize: "0.85rem",
});

const fileInputStyle = (color) => ({
  width: "100%",
  padding: "0.75rem",
  border: `2px solid ${color}`,
  borderRadius: "6px",
  fontSize: "1rem",
});

const sliderBtnStyle = (side) => ({
  position: "absolute",
  top: "50%",
  [side]: "10px",
  transform: "translateY(-50%)",
  background: "rgba(0,0,0,0.5)",
  color: "#fff",
  border: "none",
  borderRadius: "50%",
  width: "40px",
  height: "40px",
  fontSize: "24px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export default AdminInsolvencyCasePage;
